import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import SingleDropDown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Processing from 'ext-ui/components/Processing/Processing'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import { CountryCode, isSupportedCountry } from 'libphonenumber-js'
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectFetchingIssuerFromState } from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import {
  amexCardTypes,
  amexIssuerCodes,
} from 'src/app/RegisterAccount/RegisterAccount'
import AppName from 'src/components/AppName/AppName'
import RadioItems from 'src/components/RadioItems/RadioItems'
import SvgCheckLoD from 'src/components/Svgs/SvgCheckLoD'
import useAstrada from 'src/context/AstradaContext/useAstrada'
import { Issuers, IssuerSchemaFilterFields } from 'src/enums'
import { useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { PRICING_ROUTE } from 'src/navigation/urls'
import { getCountrys } from 'src/redux/actions/data/countrys.actions'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectCountryListModelsFromState,
  selectCountryOptions,
} from 'src/redux/selectors/data/countrys.selectors'
import { selectIssuerIntegratorFromState } from 'src/redux/selectors/data/issuerIntegrators.selectors'
import { css } from 'styled-components'

import * as style from './OnboardingStepFour.style'

type OnboardingStepFourFields = {
  cardholderName: string
  country: string
  address1: string
  address2: string
  city: string
  province: string
  postal: string
  cardName: string
  /** For Amex only — card type (corporate/business/not_sure) */
  cardType: string
  [key: string]: string
}

interface OnboardingStepFourProps {
  currentStep: number
  totalSteps: number
}

const services = [
  'Send secure virtual cards* to vendors, employees, and contractors',
  'Organize expenses for different teams, projects, clients, or locations',
  'Capture and auto-match receipts',
  'Automate expense tracking and reconciliation',
  'Sync transaction data to your accounting software',
]

const getPostalPlaceholder = (
  country: string,
  translate: (s: string) => string,
): string => {
  if (country === 'US') return '#####'
  if (country === 'CA') return '### ###'
  return translate('Enter Postal Code')
}

const OnboardingStepFour: React.FC<OnboardingStepFourProps> = ({
  currentStep,
  totalSteps,
}) => {
  const allCountryOptions = useSelector(selectCountryOptions)
  const countrysMap = useSelector(selectCountryListModelsFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const user = useSelector(selectUserListModelFromState)
  const selectedIssuer = useSelector(selectIssuerIntegratorFromState)
  const fetchingIssuer = useSelector(selectFetchingIssuerFromState)
  const translate = useTranslate()
  const dispatch = useDispatch()
  const history = useHistory()
  const astrada = useAstrada()
  const { analytics } = useAnalyticsContext()

  const localeCountryGuess = locale?.slice(-2)?.toUpperCase()
  const defaultCountry: string =
    localeCountryGuess && isSupportedCountry(localeCountryGuess as CountryCode)
      ? localeCountryGuess
      : 'US'

  // Issuer-specific fields (e.g. account number for partner banks). The card
  // number / expiration / CVC are captured in Step 3, so filter those out here.
  const additionalIssuerFields =
    selectedIssuer?.schema?.filter(
      (field) =>
        field.name !== IssuerSchemaFilterFields.cardNumber &&
        field.name !== IssuerSchemaFilterFields.expires &&
        field.name !== IssuerSchemaFilterFields.cvc,
    ) || []

  const initialIssuerFieldValues = additionalIssuerFields.reduce<
    Record<string, string>
  >((acc, f) => {
    acc[f.name] = ''
    return acc
  }, {})

  const { fields, setFields, fieldsErrors, setFieldsErrors } =
    useFormFields<OnboardingStepFourFields>(
      {
        cardholderName: user?.fullName || '',
        country: defaultCountry,
        address1: '',
        address2: '',
        city: '',
        province: '',
        postal: '',
        cardName: '',
        cardType: '',
        ...initialIssuerFieldValues,
      },
      () => ({
        cardholderName: [],
        country: [],
        address1: [],
        address2: [],
        city: [],
        province: [],
        postal: [],
        cardName: [],
        cardType: [],
      }),
    )

  const isAmexIssuer = amexIssuerCodes.includes(selectedIssuer?.code as Issuers)

  const selectedAmexCardTypeIndex = amexCardTypes.findIndex(
    (cardType) => cardType.value === fields.cardType,
  )
  const [amexRadioIndex, setAmexRadioIndex] = useState<number>(
    selectedAmexCardTypeIndex >= 0 ? selectedAmexCardTypeIndex : -1,
  )

  const handleAmexCardTypeChange = (index: number): void => {
    setFields({
      ...fields,
      cardType: amexCardTypes[index].value,
    })
    setAmexRadioIndex(index)
  }

  useEffect((): void => {
    analytics?.track('[onboarding] additional card info', { action: 'viewed' })
  }, [])

  // RegisterAccount.tsx dispatches getCountrys() on mount in the legacy modal
  // flow; in the in-flow Step 4 path no one upstream fetches the list, so we
  // do it here. Use the thunk (createGetCountrysInitAction alone is just an
  // action, not the API call).
  useEffect((): void => {
    if (
      allCountryOptions.length === 0 ||
      Object.keys(countrysMap).length === 0
    ) {
      dispatch(getCountrys())
    }
  }, [])

  // Mirror AddressForm: respect the issuer's country restrictions when present.
  const countryOptions = (
    selectedIssuer?.restrictions?.country !== undefined
      ? (allCountryOptions || []).filter((opt) =>
          selectedIssuer.restrictions?.country.includes(opt.value),
        )
      : allCountryOptions || []
  ).map((c) => ({
    value: c.value,
    label: c.details.text,
  }))

  const selectedCountry = countrysMap?.[fields.country]

  // The form depends on two async loads: the country list (for address fields)
  // and the issuer integrator (for partner-bank-specific fields and the Amex
  // card-type radio). Hold the form behind a spinner until both arrive so the
  // user doesn't see fields pop in one at a time. The issuer lookup may
  // legitimately resolve with no issuer (BIN range with no `issuerId`); in
  // that case the form still renders — `additionalIssuerFields` is `[]` and
  // the Amex branch is inactive, matching the legacy flow's Extend behavior.
  const countriesLoaded = Object.keys(countrysMap).length > 0
  const issuerLoaded = !fetchingIssuer
  const isLoading = !countriesLoaded || !issuerLoaded

  const validate = (): boolean => {
    const errors: Partial<Record<keyof OnboardingStepFourFields, string[]>> = {}

    if (!fields.cardholderName?.trim()) {
      errors.cardholderName = [translate('Must not be blank')]
    }
    if (!fields.country?.trim()) {
      errors.country = [translate('Must not be blank')]
    }
    if (!fields.cardName?.trim()) {
      errors.cardName = [translate('Must not be blank')]
    }

    selectedCountry?.fields?.forEach((countryField) => {
      const fieldName = countryField.name as keyof OnboardingStepFourFields
      const value = fields[fieldName]
      if (!countryField.optional && !value?.trim()) {
        errors[fieldName] = [translate('Must not be blank')]
      } else if (countryField.format && value) {
        const re = new RegExp(countryField.format)
        if (!re.test(value)) {
          errors[fieldName] = [translate('Invalid value')]
        }
      }
    })

    // Mirrors AddressForm:191-195 — the server schema marks `city` as
    // optional, but the legacy Additional Info flow has always enforced it.
    if (!fields.city?.trim()) {
      errors.city = [translate('Must not be blank')]
    }

    additionalIssuerFields.forEach((issuerField) => {
      if (issuerField.required && !fields[issuerField.name]?.trim()) {
        errors[issuerField.name as keyof OnboardingStepFourFields] = [
          translate('Must not be blank'),
        ]
      }
    })

    if (isAmexIssuer && !fields.cardType) {
      errors.cardType = [translate('Must not be blank')]
    }

    if (Object.keys(errors).length) {
      setFieldsErrors(errors)
      return false
    }
    setFieldsErrors({})
    return true
  }

  const submit = (): void => {
    if (!validate()) {
      analytics?.track('[onboarding] additional card info', {
        action: 'validation_failed',
      })
      return
    }

    const issuerData = additionalIssuerFields.reduce<Record<string, string>>(
      (acc, f) => {
        acc[f.name] = fields[f.name] ?? ''
        return acc
      },
      {},
    )

    astrada.updateState({
      cardData: astrada.cardData ? { ...astrada.cardData } : undefined,
      addressData: { ...fields },
      issuerData,
    })

    analytics?.track('[onboarding] additional card info', {
      action: 'completed',
      country: fields.country,
      has_address_2: !!fields.address2?.trim(),
      card_display_name_provided: !!fields.cardName?.trim(),
    })

    history.push(PRICING_ROUTE())
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    submit()
  }

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.currentTarget
    setFields({ ...fields, [name]: value })
  }

  // Mirror AddressForm: render country fields dynamically from the schema.
  // The Figma puts state + postal on one row; everything else (address1,
  // address2, city) is stacked above. Partition the schema fields accordingly.
  const countryFieldsAll = selectedCountry?.fields || []
  const stackedCountryFields = countryFieldsAll.filter(
    (f) => f.name !== 'province' && f.name !== 'postal',
  )
  const provinceSchemaField = countryFieldsAll.find(
    (f) => f.name === 'province',
  )
  const postalSchemaField = countryFieldsAll.find((f) => f.name === 'postal')

  const renderCountryField = (
    countryField: NonNullable<typeof selectedCountry>['fields'][number],
  ): React.ReactElement => {
    const { options, label, name } = countryField

    if (options) {
      const provinceOptions = options.map((opt) => ({
        value: opt.code,
        label: opt.label,
      }))
      const selected = provinceOptions.find(
        (opt) => opt.value === fields.province,
      )
      return (
        <SingleDropDown
          key={name}
          data-testid="state"
          selectedValue={selected}
          label={translate(label)}
          options={provinceOptions}
          searchable
          onSelect={({ value }): void => {
            setFields({ ...fields, province: value })
          }}
          errorText={fieldsErrors.province}
          placeholder={translate(`Select your ${label}`)}
        />
      )
    }

    return (
      <TextInput
        key={name}
        name={name}
        testId={name}
        label={translate(label)}
        value={fields[name] ?? ''}
        onChange={handleTextChange}
        placeholder={
          name === 'postal'
            ? getPostalPlaceholder(fields.country, translate)
            : translate(`Enter ${label}`)
        }
        errorText={fieldsErrors[name as keyof OnboardingStepFourFields]}
      />
    )
  }

  const sidePanel = (
    <div css={style.sideInfoPanel}>
      <div css={style.sideInfoInner}>
        <div css={style.sideInfoTitle}>
          {translate('Do more with your company credit card')}
        </div>
        <div css={style.sideInfoList}>
          {services.map((service) => (
            <div key={service} css={style.sideInfoServiceRow}>
              <span css={style.sideInfoServiceIcon}>
                <SvgCheckLoD />
              </span>
              <span css={style.sideInfoServiceText}>{translate(service)}</span>
            </div>
          ))}
          <div css={style.sideInfoDisclaimer}>
            {translate(
              '*Virtual card capabilities available through select bank partners.',
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div
      data-testid="onboarding-step-four"
      css={css`
        display: flex;
        flex-grow: 1;
      `}
    >
      <div css={style.formPane}>
        <div css={style.stepEyebrow}>
          {translate('Step ${currentStep} of ${totalSteps}', {
            currentStep: currentStep.toString(),
            totalSteps: totalSteps.toString(),
          })}
        </div>
        <h2
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            font-weight: 500;
            margin: 0 0 20px 0;
          `}
        >
          {translate('Add Additional Card Info')}
        </h2>
        <p
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin: 0 0 28px 0;
            max-width: 490px;
          `}
        >
          {translate(
            'Your credit card requires a little more info to connect to ${appName}. Please fill that out.',
            { appName: <AppName /> },
          )}
        </p>
        {isLoading ? (
          <div css={style.loadingContainer} data-testid="step-four-loading">
            <Processing />
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {isAmexIssuer && (
              <div css={style.amexCardTypeBlock}>
                <div css={style.amexCardTypeTitle}>
                  {translate('Card Type')}
                </div>
                <div css={style.amexCardTypeMicrocopy}>
                  {translate(
                    'Telling us your card type will help us speed up this registration',
                  )}
                </div>
                <RadioItems
                  items={amexCardTypes.map((cardType) => (
                    <span
                      key={cardType.value}
                      data-testid={`radio-button-${cardType.value.replace(/_/g, '-')}`}
                    >
                      <p
                        css={css`
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {cardType.fieldText}
                      </p>
                    </span>
                  ))}
                  currentIndex={amexRadioIndex}
                  setIndex={handleAmexCardTypeChange}
                  horizontalAlign
                  staticOnHover
                  noBorder
                  alignStart
                  gap={'40px'}
                  errors={fieldsErrors.cardType}
                  ariaLabels={amexCardTypes.map((ct) =>
                    typeof ct.fieldText === 'string'
                      ? translate(ct.fieldText)
                      : translate(ct.fieldText.props.source),
                  )}
                />
              </div>
            )}
            <div css={style.fieldGroup}>
              <TextInput
                name="cardholderName"
                testId="cardholderName"
                label={translate('Cardholder Name')}
                value={fields.cardholderName}
                onChange={handleTextChange}
                placeholder={translate('Enter Cardholder Name')}
                errorText={fieldsErrors.cardholderName}
              />
              <SingleDropDown
                data-testid="country"
                selectedValue={countryOptions.find(
                  (opt) => opt.value === fields.country,
                )}
                label={translate('Country')}
                options={countryOptions}
                searchable
                onSelect={({ value }): void => {
                  setFields({ ...fields, country: value, province: '' })
                }}
                errorText={fieldsErrors.country}
                placeholder={translate('Enter your country')}
              />
              {stackedCountryFields.map(renderCountryField)}
              <div css={style.fieldRow}>
                {provinceSchemaField && renderCountryField(provinceSchemaField)}
                {postalSchemaField && renderCountryField(postalSchemaField)}
              </div>
              {additionalIssuerFields.map((issuerField) => (
                <TextInput
                  key={issuerField.name}
                  name={issuerField.name}
                  testId={issuerField.name}
                  label={translate(issuerField.label)}
                  value={fields[issuerField.name] ?? ''}
                  onChange={handleTextChange}
                  placeholder={translateSingleString(
                    locale,
                    'Enter ${fieldLabel}',
                    { fieldLabel: issuerField.label },
                  )}
                  microcopy={issuerField.description}
                  errorText={
                    fieldsErrors[
                      issuerField.name as keyof OnboardingStepFourFields
                    ]
                  }
                />
              ))}
            </div>
            <div css={style.dividerLine} />
            <div css={style.fieldGroup}>
              <TextInput
                name="cardName"
                testId="cardName"
                maxLength={255}
                label={translate('Card Display Name')}
                value={fields.cardName}
                onChange={handleTextChange}
                placeholder={translate('Enter Card Display Name')}
                microcopy={translate(
                  "Your card's display name appears at the top of the card detail screen, as well as anywhere that refers to the card. You can update it at any time.",
                )}
                errorText={fieldsErrors.cardName}
              />
            </div>
            <div css={style.submitRow}>
              <Button type="submit" data-testid="next-button">
                {translate('Next: Select Plan')}
              </Button>
            </div>
          </form>
        )}
      </div>
      {sidePanel}
    </div>
  )
}

export default OnboardingStepFour
